import { Fragment } from "react";
import type { FieldValues, FieldPath, Control } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectGroup,
  SelectSeparator,
} from "@/components/ui/select";
import { FormTooltip } from "@/components/ui/form-description";

interface SelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  description?: string;
  descriptionAsTooltip?: boolean;
  label: string;
  name: TName;
  placeholder?: string;
  required?: boolean;
  requiredLabel?: string;
  visuallyHiddenLabel?: boolean;
  defaultValue?: string;
  options: (
    | {
        id: string;
        sectionLabel: string;
        options: { id: string; value: string; label: string }[];
        hasSeparator?: "before" | "after";
      }
    | {
        id: string;
        value: string;
        label: string;
        hasSeparator?: "before" | "after";
      }
  )[];
}

export function SelectField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  options,
  control,
  label,
  name,
  description,
  descriptionAsTooltip = false,
  placeholder,
  required,
  requiredLabel = "Required",
  visuallyHiddenLabel = false,
  className,
  defaultValue,
  ...htmlProps
}: Readonly<SelectFieldProps<TFieldValues, TName>>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        // Initialize with default value if provided and field is empty
        if (defaultValue && !field.value) {
          field.onChange(defaultValue);
        }

        return (
          <FormItem className={className} {...htmlProps}>
            <div className={cn(visuallyHiddenLabel ? "sr-only" : "")}>
              <FormLabel className="inline-block leading-snug">
                {label}
                {required ? (
                  <>
                    <span className="ms-1 text-red-500" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only">({requiredLabel})</span>
                  </>
                ) : null}
              </FormLabel>
              {description && !descriptionAsTooltip ? (
                <FormDescription>{description}</FormDescription>
              ) : null}
              {description && descriptionAsTooltip && (
                <FormTooltip description={description} />
              )}
            </div>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl className="w-full">
                <SelectTrigger>
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {options.map((option) => {
                  if ("sectionLabel" in option) {
                    return (
                      <Fragment key={option.id}>
                        {option.hasSeparator === "before" ? (
                          <SelectSeparator />
                        ) : null}
                        <SelectGroup>
                          <SelectLabel>{option.sectionLabel}</SelectLabel>
                          {option.options.map((opt) => (
                            <SelectItem key={opt.id} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        {option.hasSeparator === "after" ? (
                          <SelectSeparator />
                        ) : null}
                      </Fragment>
                    );
                  }

                  return (
                    <Fragment key={option.id}>
                      {option.hasSeparator === "before" ? (
                        <SelectSeparator />
                      ) : null}
                      <SelectItem value={option.value}>
                        {option.label}
                      </SelectItem>
                      {option.hasSeparator === "after" ? (
                        <SelectSeparator />
                      ) : null}
                    </Fragment>
                  );
                })}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
